import { useState } from 'react'
import { GoogleOutlined, LockOutlined, MailOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Row,
  theme,
  Typography,
} from 'antd'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { errorResponse } from '../../lib/errorResponse'
import { hasInvitationQueryParameter } from '../../lib/hasInvitationQueryParameter'
import { validatePasswordRule } from '../../lib/validatePasswordRule'
import { useAuth } from 'reactfire'

export default function RegistrationForm({ emailSignup, googleSignup }) {
  const [emailLoading, setEmailLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const auth = useAuth()

  const location = useLocation()
  const query = location.search

  const [searchParams] = useSearchParams()
  const [form] = Form.useForm()
  const { token } = theme.useToken()
  const { t } = useTranslation()

  async function handleEmail(values) {
    try {
      setEmailLoading(true)
      await emailSignup(values.email, values.password)
    } catch (error) {
      switch (error.code) {
        case 'auth/email-exists':
        case 'auth/email-already-in-use':
          message.error(t('frontend:auth.emailAlreadyTaken'))
          break
        case 'auth/invalid-email':
          message.error(t('frontend:auth.emailInvalid'))
          break
        case 'auth/operation-not-allowed':
          message.error(t('frontend:auth.registrationNotAllowed'))
          break
        case 'auth/weak-password':
          message.error(t('frontend:auth.weakPassword'))
          break
        default:
          errorResponse(error)
          break
      }
    } finally {
      setEmailLoading(false)
    }
  }

  async function handleGoogle() {
    setGoogleLoading(true)
    try {
      await googleSignup()
    } catch (error) {
      errorResponse(error)
    } finally {
      setGoogleLoading(false)
    }
  }

  const isInvitation = hasInvitationQueryParameter(searchParams)
  const isTaxAdvisorInvitation = searchParams.has('tax_advisor_invitation')
  const isDemoInvitation = searchParams.has('demo')

  const prefillEmail = searchParams.get('email') ?? ''

  return (
    <>
      <Card
        bordered={false}
        styles={{ body: { paddingBottom: '32px', paddingTop: '32px' } }}
      >
        <Row gutter={[0, 5]}>
          {isInvitation && (
            <Col span={24}>
              <>
                <Typography.Title level={3}>
                  {t(
                    isTaxAdvisorInvitation
                      ? 'frontend:auth.invitationTitleFormal'
                      : 'frontend:auth.invitationTitleInformal',
                    {
                      kind: isDemoInvitation
                        ? t('frontend:auth.demo')
                        : t('frontend:auth.invitation'),
                    }
                  )}
                </Typography.Title>
                <Typography.Paragraph style={{ padding: '0 20px' }}>
                  {t(
                    isTaxAdvisorInvitation
                      ? 'frontend:auth.registrationInviteIntroFormal'
                      : 'frontend:auth.registrationInviteIntroInformal'
                  )}
                </Typography.Paragraph>
              </>
              <Divider
                style={{
                  color: token.colorTextTertiary,
                  fontWeight: '500',
                  fontSize: '14px',
                }}
              />
            </Col>
          )}
          <Col span={24}>
            <Button
              loading={googleLoading}
              icon={<GoogleOutlined />}
              type="primary"
              danger
              block
              onClick={handleGoogle}
            >
              {t('frontend:auth.signInWithGoogle')}
            </Button>
          </Col>
          <Col span={24}>
            <Divider
              style={{
                color: token.colorTextTertiary,
                fontWeight: '500',
                fontSize: '14px',
              }}
            >
              {t('frontend:auth.or')}
            </Divider>
          </Col>
          <Col span={24}>
            <Form
              form={form}
              onFinish={handleEmail}
              className="login-form"
              initialValues={{ email: prefillEmail }}
            >
              <Form.Item
                style={{ marginBottom: token.marginXS }}
                name="email"
                validateDebounce={1000}
                rules={[
                  {
                    required: true,
                    message: t('frontend:auth.emailRequired'),
                  },
                  {
                    type: 'email',
                    message: t('frontend:auth.emailInvalidExclaim'),
                  },
                ]}
              >
                <Input
                  prefix={
                    <MailOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  placeholder="Email"
                  type="email"
                />
              </Form.Item>
              <Form.Item
                name="password"
                validateDebounce={1000}
                style={{ marginBottom: token.marginXS }}
                rules={[
                  {
                    required: true,
                    message: t('frontend:auth.passwordRequired'),
                  },
                  validatePasswordRule(auth),
                ]}
              >
                <Input
                  prefix={
                    <LockOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  type="password"
                  placeholder={t('frontend:auth.password')}
                />
              </Form.Item>
              <Form.Item
                name={['confirmPassword']}
                validateDebounce={1000}
                rules={[
                  {
                    required: true,
                    message: t('frontend:auth.confirmPasswordRequired'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(
                        new Error(t('frontend:auth.confirmationMismatch'))
                      )
                    },
                  }),
                ]}
              >
                <Input
                  prefix={
                    <LockOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  type="password"
                  placeholder={t('frontend:auth.confirmPasswordPlaceholder')}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: '10px' }}>
                <Button
                  loading={emailLoading}
                  block
                  type="primary"
                  htmlType="submit"
                  className="login-form-button"
                >
                  {t('frontend:auth.createAccount')}
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Card>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginTop: '15px' }}
      >
        {t(
          isTaxAdvisorInvitation
            ? 'frontend:auth.alreadyHaveAccountFormal'
            : 'frontend:auth.alreadyHaveAccountInformal'
        )}{' '}
        <Link to={`/login${query}`}>{t('frontend:auth.login')}</Link>
      </Typography.Text>
    </>
  )
}
